import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Turnstile } from "@/components/Turnstile";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { buildBugReport, type BugReport } from "@/lib/bugReport";
import { submitMessage } from "@/lib/contactApi";

/**
 * Manual bug-report dialog. The diagnostic payload is auto-gathered and shown to
 * the user in full; nothing is sent until they tick "I've reviewed this" and
 * submit. There is no auto-send anywhere in the app.
 */

export interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BugReportDialog({ open, onOpenChange }: BugReportDialogProps): JSX.Element {
  const [trying, setTrying] = useState("");
  const [happened, setHappened] = useState("");
  const [expected, setExpected] = useState("");
  const [base, setBase] = useState<BugReport | null>(null);
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStatus("idle");
    setConsent(false);
    setToken(null);
    setError(null);
    void buildBugReport({ trying: "", happened: "", expected: "" }).then(setBase);
  }, [open]);

  const report = useMemo<BugReport | null>(
    () => (base ? { ...base, user: { trying, happened, expected } } : null),
    [base, trying, happened, expected],
  );

  const canSend = consent && token !== null && report !== null && status !== "sending";

  async function send(): Promise<void> {
    if (!report || !token) return;
    setStatus("sending");
    setError(null);
    const result = await submitMessage({ type: "bug_report", report, turnstileToken: token });
    if (result.ok) {
      setStatus("sent");
      toast.success("Bug report sent — thank you!");
      onOpenChange(false);
    } else {
      setStatus("idle");
      setError(result.message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-auto">
        <DialogHeader>
          <DialogTitle>Report an issue</DialogTitle>
          <DialogDescription>
            Nothing is sent automatically. Review the full payload below and confirm before sending.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="br-trying">What were you trying to do?</Label>
            <Textarea id="br-trying" rows={2} value={trying} onChange={(e) => setTrying(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="br-happened">What happened?</Label>
            <Textarea id="br-happened" rows={2} value={happened} onChange={(e) => setHappened(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="br-expected">What did you expect to happen?</Label>
            <Textarea id="br-expected" rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Exact data that will be sent</Label>
          <pre className="max-h-56 overflow-auto rounded-md border bg-muted/50 p-3 text-xs">
            {report ? JSON.stringify(report, null, 2) : "Gathering diagnostics…"}
          </pre>
          <p className="text-xs text-muted-foreground">
            This includes your browser info, app version, current URL, the last 20 audit entries, any
            captured console errors, and a storage summary (counts and sizes only — never catalog
            contents).
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          I&apos;ve reviewed the payload above and want to send it.
        </label>

        <Turnstile onToken={setToken} onExpire={() => setToken(null)} />

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={status === "sending"}>
            Cancel
          </Button>
          <Button onClick={() => void send()} disabled={!canSend}>
            {status === "sending" ? "Sending…" : "I've reviewed this, send it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
